// Cattle Forecast — Supabase read/write wrappers.
//
// Pure math + state shape lives in src/lib/cattleForecast.js; this module
// owns the side-effectful loads + writes against the three forecast tables
// (mig 043) plus the helpers for converting in-memory state back into the
// row shape expected on disk.
//
// All functions throw on Supabase error so callers handle failure with the
// same try/catch pattern used elsewhere in the cattle module. The view
// layer surfaces error messages to the operator.
//
// Migration 240 (R4): every WRITE here is the audited
// `cattle_batch_operation` RPC through src/lib/cattleOperationsApi.ts — the
// server writes each operation's Activity row and an immutable request
// receipt in the write's own transaction, so this module has no client-side
// Activity emissions. The read wrappers are unchanged.

import {todayCentralISO} from './dateUtils.js';
import {loadCattleWeighInsCached} from './cattleCache.js';
import {buildForecast, dateToMonthKey, projectPlannedRoster} from './cattleForecast.js';
import {newCattleRequestId, runCattleOperation} from './cattleOperationsApi.js';
import type {
  CattleProcessingBatchRow,
  ForecastHiddenRow,
  ForecastResult,
  ForecastSettings,
  ForecastWeighInRow,
  ProjectedRosterResult,
} from './cattleForecast.js';
import type {CattleRecord} from './cattleHerdFilters.js';
import type {SupabaseClient} from './supabaseTypes.js';

const SETTINGS_PK = 'global';

// ── the rows these reads select ─────────────────────────────────────────────

// cattle_forecast_settings (mig 043) as stored. loadForecastSettings maps this
// onto the camelCase ForecastSettings shape buildForecast consumes; the two are
// deliberately separate types because only one of them is a table.
export type ForecastSettingsRow = {
  display_weight_min?: number | null;
  display_weight_max?: number | null;
  fallback_adg_lb_per_day?: number | string | null;
  birth_weight_lb?: number | string | null;
  horizon_years?: number | null;
  monthly_capacity?: number | null;
  included_herds?: readonly string[] | null;
  updated_at?: string | null;
  updated_by?: string | null;
};

// Everything buildForecast needs, loaded once. `settings` is already in the
// in-memory camelCase shape; `includes` is the id set; `hidden` is the raw rows.
export type CattleForecastBundle = {
  cattle: CattleRecord[];
  weighIns: ForecastWeighInRow[];
  settings: ForecastSettings;
  includes: Set<string>;
  hidden: ForecastHiddenRow[];
  batches: CattleProcessingBatchRow[];
  realBatches: CattleProcessingBatchRow[];
  scheduledBatches: CattleProcessingBatchRow[];
};

export type ProjectedRosterForBatch =
  | {ok: false; reason: string; batch?: CattleProcessingBatchRow}
  | {
      ok: true;
      batch: CattleProcessingBatchRow;
      monthKey: string;
      roster: ProjectedRosterResult;
      sequenceName: string | null | undefined;
    };

// ── settings ─────────────────────────────────────────────────────────────────

// Returns the in-memory shape consumed by buildForecast({...settings}).
// Maps DB column names to the camelCase keys the helper expects, applying
// safe defaults for nulls / unset columns.
function settingsRowToState(row: ForecastSettingsRow | null | undefined): ForecastSettings | null {
  if (!row) return null;
  return {
    displayMin: row.display_weight_min,
    displayMax: row.display_weight_max,
    // parseFloat stringifies its argument; the numeric columns arrive as either
    // a number or the string PostgREST sends for a numeric type.
    fallbackAdg: parseFloat(String(row.fallback_adg_lb_per_day)),
    birthWeight: parseFloat(String(row.birth_weight_lb)),
    horizonYears: row.horizon_years,
    monthlyCapacity: row.monthly_capacity,
    includedHerds: Array.isArray(row.included_herds) ? row.included_herds : [],
    updatedAt: row.updated_at,
    updatedBy: row.updated_by,
  };
}

// (settingsStateToRow is gone with migration 240: the save is the audited
// forecast_settings.save operation; the row is server-built and stamped.)

export function forecastSettingsFromRow(row: ForecastSettingsRow | null | undefined): ForecastSettings {
  const state =
    settingsRowToState(row) ||
    settingsRowToState({
      display_weight_min: 1200,
      display_weight_max: 1500,
      fallback_adg_lb_per_day: 1.18,
      birth_weight_lb: 64,
      horizon_years: 3,
      monthly_capacity: null,
      included_herds: ['finishers', 'backgrounders'],
    });
  if (!state) throw new Error('loadForecastSettings: default settings state could not be built');
  return state;
}

export async function loadForecastSettings(sb: SupabaseClient): Promise<ForecastSettings> {
  const r = await sb.from('cattle_forecast_settings').select('*').eq('id', SETTINGS_PK).maybeSingle();
  if (r.error) throw new Error('loadForecastSettings: ' + r.error.message);
  return forecastSettingsFromRow(r.data || null);
}

// The two NUMERIC columns come back from PostgREST as JSON strings ("1.18")
// while the operation's stored jsonb holds JSON numbers — the op compares
// JSON exactly, so both sides of the compare are coerced through the same
// rule here. Int columns and text stay as-is (they round-trip as numbers and
// strings respectively).
function settingsNumber(v: ForecastSettings['fallbackAdg']): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Save the settings singleton through the audited forecast_settings.save
 * operation. `expected` is the LOADED settings state as this surface read it
 * (before the user edited the draft); a concurrent save lands as a conflict
 * and the caller's refetch shows the winner.
 */
export async function saveForecastSettings(
  sb: SupabaseClient,
  state: ForecastSettings,
  expected: ForecastSettings,
): Promise<void> {
  const toChange = (s: ForecastSettings) => ({
    display_weight_min: s.displayMin,
    display_weight_max: s.displayMax,
    fallback_adg_lb_per_day: settingsNumber(s.fallbackAdg),
    birth_weight_lb: settingsNumber(s.birthWeight),
    horizon_years: s.horizonYears,
    monthly_capacity:
      s.monthlyCapacity != null && String(s.monthlyCapacity) !== '' ? parseInt(String(s.monthlyCapacity), 10) : null,
    included_herds: Array.isArray(s.includedHerds) ? [...s.includedHerds] : [],
  });
  await runCattleOperation(sb, {
    requestId: newCattleRequestId(),
    operation: 'forecast_settings.save',
    target: {},
    expected: toChange(expected),
    change: toChange(state),
  });
}

// ── heifer includes ──────────────────────────────────────────────────────────

// `select('cattle_id')` — one column, so the row type is exactly that column.
export type HeiferIncludeRow = {cattle_id: string};

export async function loadHeiferIncludes(sb: SupabaseClient): Promise<Set<string>> {
  const r = await sb.from('cattle_forecast_heifer_includes').select('cattle_id');
  if (r.error) throw new Error('loadHeiferIncludes: ' + r.error.message);
  const rows: HeiferIncludeRow[] = r.data || [];
  return new Set(rows.map((row) => row.cattle_id));
}

// Replace the entire set on Confirm Selections. Empty set is a valid save.
// Migration 240: ONE heifer.apply operation per Confirm (root's C2) — the
// diff is computed here exactly as before (unchanged rows untouched, keeping
// their included_at/included_by audit), then applied atomically with the
// caller's read set as the expected-before; included_at/included_by are
// server-stamped now.
export async function saveHeiferIncludes(
  sb: SupabaseClient,
  nextSet: ReadonlySet<string> | readonly string[] | null | undefined,
): Promise<void> {
  const cur = await loadHeiferIncludes(sb);
  const next: ReadonlySet<string> = nextSet instanceof Set ? nextSet : new Set(nextSet || []);
  const toAdd = [...next].filter((id) => !cur.has(id));
  const toRemove = [...cur].filter((id) => !next.has(id));
  await runCattleOperation(sb, {
    requestId: newCattleRequestId(),
    operation: 'heifer.apply',
    target: {},
    expected: {included: [...cur]},
    change: {add: toAdd, remove: toRemove},
  });
}

// ── per-cow per-month hidden ──────────────────────────────────────────────────

// The four columns `select('cattle_id, month_key, hidden_at, hidden_by')` names.
export type ForecastHiddenSelectedRow = ForecastHiddenRow & {
  hidden_at?: string | null;
  hidden_by?: string | null;
};

export async function loadHidden(sb: SupabaseClient): Promise<ForecastHiddenSelectedRow[]> {
  const r = await sb.from('cattle_forecast_hidden').select('cattle_id, month_key, hidden_at, hidden_by');
  if (r.error) throw new Error('loadHidden: ' + r.error.message);
  return r.data || [];
}

// Migration 240: hide/unhide is ONE hidden.set operation per toggle. The
// caller passes the toggle's read state (`currentlyHidden`); the operation
// applies only from that state and writes the client-mirrored status.changed
// Activity row itself, so there is no client-side Activity here anymore.
export async function setHidden(
  sb: SupabaseClient,
  {cattleId, monthKey, currentlyHidden}: {cattleId: string; monthKey: string; currentlyHidden: boolean},
): Promise<void> {
  await runCattleOperation(sb, {
    requestId: newCattleRequestId(),
    operation: 'hidden.set',
    target: {cattle_id: cattleId, month_key: monthKey},
    expected: {hidden: currentlyHidden},
    change: {},
  });
}

// Every processing-batch row, unpartitioned and SOFT-FAILING TO EMPTY.
//
// The soft fail is preserved deliberately and it is NOT an endorsement. This is
// the read CattleSendToProcessorModal issued inline, as the fourth member of a
// `Promise.all` whose other three (loadForecastSettings / loadHeiferIncludes /
// loadHidden) all THROW on error. The modal consumed it as `batchesR.data ||
// []`, so a DB failure on this one leg silently yields zero batch history and
// the forecast is computed as though the farm had never processed an animal —
// rather than the modal's own "Could not load forecast" surface, which its
// siblings would have produced.
//
// Making this throw like `loadCattleForecastBundle` does (see its explicit
// `batchesR.error` check below) is arguably the fix. It is not made here
// because it is a BEHAVIOUR CHANGE inside a send-to-processor path, and the
// move that brought this chain into a lib module may not smuggle one in. The
// defect is recorded so a later change is a decision rather than a discovery.
export async function loadCattleProcessingBatchRows(sb: SupabaseClient): Promise<CattleProcessingBatchRow[]> {
  const r = await sb.from('cattle_processing_batches').select('*');
  return r.data || [];
}

// A weigh-in row as the Send-to-Processor forecast reads it: the forecast's
// fields plus the identity this loader merges on.
export type SendForecastWeighInRow = ForecastWeighInRow & {id?: string | null; session_id?: string | null};

// The weigh-in history CattleSendToProcessorModal forecasts from when its
// caller does not hold one. The record page passes the full cattle history it
// already loaded; /weighins holds only the open session's rows. It used to pass
// [], which dropped every no-DOB animal from the proposed batch and projected
// the rest from DOB + birth weight instead of their measured weights.
//
// FAIL-CLOSED, like loadCattleForecastBundle: a failed history read throws, so
// the modal shows "Could not load forecast" rather than DOB projections.
//
// The open session is authoritative for its own rows. The history is cached
// for 30 s, so it can miss a weight entered moments ago or still hold one since
// corrected or deleted: every history row of that session is replaced by the
// session's current entries. The result is a NEW array; the cached one is
// shared with other readers and is never mutated.
export async function loadSendForecastWeighIns(
  sb: SupabaseClient,
  session: {id?: string | null; entries?: readonly SendForecastWeighInRow[] | null},
): Promise<SendForecastWeighInRow[]> {
  const history = (await loadCattleWeighInsCached(sb, {throwOnError: true})) as SendForecastWeighInRow[];
  const entries = (session.entries || []).filter(Boolean);
  const sessionId = session.id ? String(session.id) : null;
  const entryIds = new Set(entries.map((e) => (e.id == null ? '' : String(e.id))).filter(Boolean));
  const merged = history.filter(
    (row) =>
      !!row &&
      !(sessionId && row.session_id != null && String(row.session_id) === sessionId) &&
      !(row.id != null && entryIds.has(String(row.id))),
  );
  merged.push(...entries);
  // The cattleCache contract: newest first by entered_at.
  merged.sort((a, b) => String(b.entered_at || '').localeCompare(String(a.entered_at || '')));
  return merged;
}

// ── shared forecast bundle + projected-roster loader ─────────────────────────

// Load every input buildForecast needs, in one place, for the surfaces that
// don't already hold them (cattle batch record page, forecast-only batch
// detail, Processing Drawer cattle Source details). FAIL-CLOSED: any read
// error throws — callers must render an explicit unavailable state instead
// of projecting from partial inputs (a missing hidden/includes/settings read
// would silently change cohort membership).
export async function loadCattleForecastBundle(sb: SupabaseClient): Promise<CattleForecastBundle> {
  const [cattleR, weighIns, settings, includes, hidden, batchesR] = await Promise.all([
    sb.from('cattle').select('*').is('deleted_at', null),
    // throwOnError: a raced/errored weigh-ins read must fail this loader —
    // the soft [] fallback would silently project every cow from its DOB
    // fallback (fabricated weights) instead of real weigh-in anchors.
    loadCattleWeighInsCached(sb, {throwOnError: true}),
    loadForecastSettings(sb),
    loadHeiferIncludes(sb),
    loadHidden(sb),
    sb.from('cattle_processing_batches').select('*'),
  ]);
  if (cattleR.error) throw new Error('loadCattleForecastBundle (cattle): ' + cattleR.error.message);
  if (batchesR.error) throw new Error('loadCattleForecastBundle (batches): ' + batchesR.error.message);
  const batches: CattleProcessingBatchRow[] = batchesR.data || [];
  return {
    cattle: cattleR.data || [],
    weighIns: weighIns || [],
    settings,
    includes,
    hidden,
    batches,
    realBatches: batches.filter((b) => b.status === 'active' || b.status === 'complete'),
    scheduledBatches: batches.filter((b) => b.status === 'scheduled'),
  };
}

// Build the forecast from a loaded bundle. Thin composition helper so every
// caller passes the SAME partitioned inputs (no surface can accidentally
// leak scheduled rows into realBatches or vice versa).
export function forecastFromBundle(
  bundle: CattleForecastBundle,
  {todayMs = Date.now()}: {todayMs?: number} = {},
): ForecastResult {
  return buildForecast({
    cattle: bundle.cattle,
    weighIns: bundle.weighIns,
    settings: bundle.settings,
    includes: bundle.includes,
    hidden: bundle.hidden,
    realBatches: bundle.realBatches,
    scheduledBatches: bundle.scheduledBatches,
    todayMs,
  });
}

// Projected roster for one PERSISTED scheduled batch, resolved through the
// canonical forecast math (buildForecast → projectPlannedRoster). Used by the
// cattle batch record page and the Processing Drawer so both render the exact
// rows/totals the consolidated Planned list shows. Returns:
//   {ok:true, batch, monthKey, roster, sequenceName}   — roster from
//       projectPlannedRoster; sequenceName is the reconciliation-derived
//       display name (may differ from the stored name until the management
//       reconcile pass lands; stored name is authoritative for the record).
//   {ok:false, reason, batch?}                          — fail-closed.
export async function loadProjectedRosterForScheduledBatch(
  sb: SupabaseClient,
  batchId: string,
  {todayMs = Date.now()}: {todayMs?: number} = {},
): Promise<ProjectedRosterForBatch> {
  const bundle = await loadCattleForecastBundle(sb);
  const batch = bundle.batches.find((b) => b && b.id === batchId) || null;
  if (!batch) return {ok: false, reason: 'batch_not_found'};
  if (batch.status !== 'scheduled') return {ok: false, reason: 'not_scheduled', batch};
  const monthKey = dateToMonthKey(batch.planned_process_date);
  if (!monthKey) return {ok: false, reason: 'no_planned_date', batch};
  const forecast = forecastFromBundle(bundle, {todayMs});
  const roster = projectPlannedRoster(forecast, monthKey);
  if (!roster.ok) return {ok: false, reason: roster.reason, batch};

  const enriched = (forecast.scheduledBatches || []).find((s) => s && s.id === batchId) || null;
  return {ok: true, batch, monthKey, roster, sequenceName: enriched ? enriched.name : batch.name};
}

// ── batch state-machine helpers ───────────────────────────────────────────────

// Mark an active batch complete through the audited batch.complete operation
// (migration 240). The caller still verifies every cow has hanging_weight > 0
// first (batchHasAllHangingWeights) for its own notice; the server enforces
// the same rule. actual_process_date resolves to the caller-supplied date when
// truthy, otherwise the server's FARM date (America/Chicago) — never null, so
// the Processed section always renders a date.
export async function markBatchComplete(
  sb: SupabaseClient,
  batchId: string,
  {processedDate}: {processedDate?: string | null} = {},
): Promise<void> {
  await runCattleOperation(sb, {
    requestId: newCattleRequestId(),
    operation: 'batch.complete',
    target: {batch_id: batchId},
    expected: {status: 'active'},
    change: processedDate ? {actual_process_date: processedDate} : {},
  });
}

// Reopen a complete batch back to active through batch.reopen. The operation
// keeps existing cows_detail + hanging weights and does NOT clear
// actual_process_date — today's rule, now server-side.
export async function reopenBatch(sb: SupabaseClient, batchId: string): Promise<void> {
  await runCattleOperation(sb, {
    requestId: newCattleRequestId(),
    operation: 'batch.reopen',
    target: {batch_id: batchId},
    expected: {status: 'complete'},
    change: {},
  });
}
